import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PrintPreview, DocHeader, Grid } from "@/components/PrintDoc";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy } from "@/lib/format";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];
const METHODS = ["bank", "cash", "upi", "card", "cheque", "other"];

export default function Payroll() {
  const { can, user } = useAuth();
  const isAdmin = ["business_admin", "hospital_admin", "pharmacy_admin", "platform_owner"].includes(user?.role);
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [prep, setPrep] = useState(null);
  const [lines, setLines] = useState([]);
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);
  const [slip, setSlip] = useState(null);
  const [business, setBusiness] = useState(null);
  const [salaryFor, setSalaryFor] = useState(null);
  const [salaryForm, setSalaryForm] = useState({ amount: "", frequency: "monthly",
    effective_from: new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }), notes: "" });

  const load = useCallback(async () => {
    setPrep(null);
    try {
      const [p, existing] = await Promise.all([
        api.get("/payroll/prepare", { params: { month, year } }),
        api.get("/payroll", { params: { month, year } }),
      ]);
      setPrep(p.data);
      setSaved(existing.data.payroll);
      const existingLines = existing.data.payroll?.lines || [];
      setLines(p.data.lines.map((l) => {
        const prev = existingLines.find((x) => x.user_id === l.user_id);
        return {
          user_id: l.user_id, name: l.name, role: l.role, attendance: l.attendance,
          gross_salary: String(prev?.gross_salary ?? l.gross_salary ?? 0),
          additions: String(prev?.additions ?? 0),
          deductions: String(prev?.deductions ?? l.suggested_deductions ?? 0),
          addition_note: prev?.addition_note || "", deduction_note: prev?.deduction_note || "",
          payment_status: prev?.payment_status || "unpaid",
          payment_method: prev?.payment_method || "bank",
          payment_date: (prev?.payment_date || "").slice(0, 10),
          reference: prev?.reference || "",
        };
      }));
    } catch (e) { toast.error(apiError(e)); }
  }, [month, year]);

  useEffect(() => {
    if (!can("payroll.view")) return;
    load();
    api.get("/settings/full").then(({ data }) => setBusiness(data.business || null)).catch(() => {});
  }, [load, can]);

  if (!can("payroll.view")) return <NoAccess what="payroll" />;

  const set = (i, patch) => setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const net = (l) => Number(l.gross_salary || 0) + Number(l.additions || 0) - Number(l.deductions || 0);
  const totalNet = lines.reduce((s, l) => s + net(l), 0);
  const locked = saved?.locked;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payroll", { month, year,
        lines: lines.map((l) => ({ user_id: l.user_id, gross_salary: Number(l.gross_salary || 0),
          additions: Number(l.additions || 0), deductions: Number(l.deductions || 0),
          addition_note: l.addition_note, deduction_note: l.deduction_note,
          payment_status: l.payment_status, payment_method: l.payment_method,
          payment_date: l.payment_date, reference: l.reference })) });
      setSaved(data.payroll);
      toast.success(`Payroll for ${MONTHS[month - 1]} ${year} saved — net ${inr(data.payroll.totals.net)}`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const close = async () => {
    try {
      await api.post(`/payroll/${year}-${String(month).padStart(2, "0")}/close`);
      toast.success("Payroll closed. Historical records are now frozen.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openSlip = async (userId) => {
    try {
      const { data } = await api.get(
        `/payroll/${year}-${String(month).padStart(2, "0")}/slip/${userId}`);
      setSlip(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const sendWhatsapp = async () => {
    try {
      await api.post(`/payroll/${slip.period}/slip/${slip.staff.user_id}/whatsapp`);
      toast.success("Salary slip queued for WhatsApp.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveSalary = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/staff/${salaryFor.user_id}/salary`, {
        amount: Number(salaryForm.amount || 0), frequency: salaryForm.frequency,
        effective_from: salaryForm.effective_from, notes: salaryForm.notes });
      toast.success("Salary recorded. Past payroll runs are unchanged.");
      setSalaryFor(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="payroll-page">
      <PageHeader overline="Business" title="Payroll"
        subtitle="Monthly salary run with attendance-based suggestions, salary slips and payment tracking. Closing a month freezes it."
        actions={can("payroll.manage") && !locked && (
          <>
            <button onClick={save} disabled={busy} className={primaryBtnCls} data-testid="payroll-save-btn">
              Save payroll
            </button>
            {isAdmin && saved && (
              <button onClick={close} className={ghostBtnCls} data-testid="payroll-close-btn">Close month</button>
            )}
          </>
        )} />

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <select className={`${inputCls} sm:!w-44`} value={month} data-testid="payroll-month-select"
          onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <input type="number" className={`${inputCls} sm:!w-28`} value={year} data-testid="payroll-year-input"
          onChange={(e) => setYear(Number(e.target.value))} />
        {locked && (
          <span className="rounded-full bg-slate-900 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white"
            data-testid="payroll-locked-badge">Closed — read only</span>
        )}
      </div>

      {!prep ? <Loading /> : lines.length === 0 ? (
        <EmptyState title="No active staff" subtitle="Add staff members with a salary to run payroll." testid="payroll-empty" />
      ) : (
        <>
          <div className="mb-6 grid gap-3 rounded-lg bg-[#0F172A] p-5 text-white sm:grid-cols-4">
            <div><p className="overline text-slate-400">Staff</p>
              <p className="mt-1 font-semibold" data-testid="payroll-staff-count">{lines.length}</p></div>
            <div><p className="overline text-slate-400">Gross</p>
              <p className="mt-1 font-semibold">{inr(lines.reduce((s, l) => s + Number(l.gross_salary || 0), 0))}</p></div>
            <div><p className="overline text-slate-400">Deductions</p>
              <p className="mt-1 font-semibold">{inr(lines.reduce((s, l) => s + Number(l.deductions || 0), 0))}</p></div>
            <div><p className="overline text-[#EA580C]">Net payable</p>
              <p className="mt-1 font-ui text-xl font-bold" data-testid="payroll-net-total">{inr(totalNet)}</p></div>
          </div>

          <div className="space-y-3">
            {lines.map((l, i) => (
              <div key={l.user_id} className="rounded-lg border border-slate-200 bg-slate-50/60 p-4"
                data-testid={`payroll-line-${l.user_id}`}>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-slate-900">{l.name}
                      <span className="ml-2 text-xs uppercase tracking-wider text-slate-400">{l.role}</span></p>
                    <p className="text-xs text-slate-500">
                      Present {l.attendance.present} · Paid leave {l.attendance.paid_leave} ·
                      {" "}Absent {l.attendance.absent} of {l.attendance.days_in_month} days
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {can("payroll.manage") && (
                      <button onClick={() => { setSalaryFor(l); setSalaryForm({ ...salaryForm,
                        amount: String(l.gross_salary) }); }} className={ghostBtnCls}
                        data-testid={`set-salary-${l.user_id}`}>Set salary</button>
                    )}
                    {saved && (
                      <button onClick={() => openSlip(l.user_id)} className={ghostBtnCls}
                        data-testid={`slip-${l.user_id}`}>Salary slip</button>
                    )}
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-7">
                  <Field label="Gross">
                    <input type="number" min="0" disabled={locked} className={inputCls} value={l.gross_salary}
                      data-testid={`payroll-gross-${l.user_id}`}
                      onChange={(e) => set(i, { gross_salary: e.target.value })} />
                  </Field>
                  <Field label="Additions">
                    <input type="number" min="0" disabled={locked} className={inputCls} value={l.additions}
                      data-testid={`payroll-additions-${l.user_id}`}
                      onChange={(e) => set(i, { additions: e.target.value })} />
                  </Field>
                  <Field label="Deductions">
                    <input type="number" min="0" disabled={locked} className={inputCls} value={l.deductions}
                      data-testid={`payroll-deductions-${l.user_id}`}
                      onChange={(e) => set(i, { deductions: e.target.value })} />
                  </Field>
                  <Field label="Status">
                    <select disabled={locked} className={inputCls} value={l.payment_status}
                      data-testid={`payroll-status-${l.user_id}`}
                      onChange={(e) => set(i, { payment_status: e.target.value })}>
                      <option value="unpaid">Unpaid</option>
                      <option value="paid">Paid</option>
                    </select>
                  </Field>
                  <Field label="Method">
                    <select disabled={locked} className={inputCls} value={l.payment_method}
                      data-testid={`payroll-method-${l.user_id}`}
                      onChange={(e) => set(i, { payment_method: e.target.value })}>
                      {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </Field>
                  <Field label="Payment date">
                    <input type="date" disabled={locked} className={inputCls} value={l.payment_date}
                      onChange={(e) => set(i, { payment_date: e.target.value })} />
                  </Field>
                  <Field label="Txn / reference">
                    <input disabled={locked} className={inputCls} value={l.reference}
                      data-testid={`payroll-reference-${l.user_id}`}
                      onChange={(e) => set(i, { reference: e.target.value })} />
                  </Field>
                </div>
                <p className="mt-3 text-sm">
                  Net salary: <span className="font-semibold text-slate-900"
                    data-testid={`payroll-net-${l.user_id}`}>{inr(net(l))}</span>
                </p>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal open={Boolean(salaryFor)} onClose={() => setSalaryFor(null)} title="Set salary"
        subtitle={salaryFor?.name} testid="salary-modal">
        <form onSubmit={saveSalary} className="grid gap-5 sm:grid-cols-2">
          <Field label="Salary amount (₹) *">
            <input required type="number" min="0" className={inputCls} value={salaryForm.amount}
              data-testid="salary-amount-input"
              onChange={(e) => setSalaryForm({ ...salaryForm, amount: e.target.value })} />
          </Field>
          <Field label="Frequency">
            <select className={inputCls} value={salaryForm.frequency} data-testid="salary-frequency-select"
              onChange={(e) => setSalaryForm({ ...salaryForm, frequency: e.target.value })}>
              {["monthly", "weekly", "daily", "hourly"].map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Effective from *">
            <input required type="date" className={inputCls} value={salaryForm.effective_from}
              data-testid="salary-effective-input"
              onChange={(e) => setSalaryForm({ ...salaryForm, effective_from: e.target.value })} />
          </Field>
          <Field label="Notes">
            <input className={inputCls} value={salaryForm.notes}
              onChange={(e) => setSalaryForm({ ...salaryForm, notes: e.target.value })} />
          </Field>
          <div className="sm:col-span-2">
            <p className="mb-4 text-xs text-slate-500">
              Previous salary records are kept, so payroll already run for earlier months stays exactly as it was.
            </p>
            <button className={primaryBtnCls} data-testid="salary-submit-btn">Save salary</button>
          </div>
        </form>
      </Modal>

      <PrintPreview open={Boolean(slip)} onClose={() => setSlip(null)}
        title={`Salary slip ${slip?.slip?.slip_no || ""}`}
        actions={can("payroll.manage") && (
          <button onClick={sendWhatsapp} className={ghostBtnCls} data-testid="slip-whatsapp-btn">
            Send on WhatsApp
          </button>
        )}
        testid="salary-slip-preview">
        {slip && (
          <>
            <DocHeader business={business} docTitle="Salary Slip"
              right={<>
                <p className="text-[11px]">No: <b>{slip.slip.slip_no}</b></p>
                <p className="text-[11px]">{MONTHS[slip.month - 1]} {slip.year}</p>
              </>} />
            <Grid rows={[["Staff name", slip.slip.name], ["Role", slip.slip.role],
              ["Employee ID", slip.slip.employee_id || "—"],
              ["Department", slip.slip.department || "—"],
              ["Payroll period", `${MONTHS[slip.month - 1]} ${slip.year}`],
              ["Payment status", slip.slip.payment_status === "paid" ? "Paid" : "Unpaid"],
              ["Payment method", slip.slip.payment_method || "—"],
              ["Transaction / reference", slip.slip.reference || "—"],
              ["Payment date", slip.slip.payment_date ? dmy(slip.slip.payment_date) : "—"]]} />
            <table className="mt-6 w-full border-collapse text-[12px]">
              <tbody>
                <tr className="border-y-2 border-slate-800">
                  <td className="py-1.5 font-semibold">Earnings</td>
                  <td className="py-1.5 text-right font-semibold">Amount</td>
                </tr>
                <tr className="border-b border-slate-200"><td className="py-1.5">Gross salary</td>
                  <td className="py-1.5 text-right">{inr(slip.slip.gross_salary)}</td></tr>
                <tr className="border-b border-slate-200">
                  <td className="py-1.5">Additions {slip.slip.addition_note ? `(${slip.slip.addition_note})` : ""}</td>
                  <td className="py-1.5 text-right">{inr(slip.slip.additions)}</td></tr>
                <tr className="border-b border-slate-200">
                  <td className="py-1.5">Deductions {slip.slip.deduction_note ? `(${slip.slip.deduction_note})` : ""}</td>
                  <td className="py-1.5 text-right">- {inr(slip.slip.deductions)}</td></tr>
                <tr className="border-y-2 border-slate-800 font-ui text-[14px] font-bold">
                  <td className="py-1.5">Net salary</td>
                  <td className="py-1.5 text-right">{inr(slip.slip.net_salary)}</td></tr>
              </tbody>
            </table>
            <p className="mt-8 text-[10px] text-slate-500">
              This is a computer-generated salary slip issued by {business?.name || "the business"}.
            </p>
          </>
        )}
      </PrintPreview>
    </div>
  );
}
